import { useCallback, DragEvent } from 'react';
import useSetState from 'react-use/lib/useSetState';

type Payload = {
    draggedFrom: number | null;
    draggedTo: number | null;
    isDragging: boolean;
};

const INITIAL_PAYLOAD: Payload = {
    draggedFrom: null,
    draggedTo: null,
    isDragging: false,
};

interface UseDragAndDropProps {
    onDrop: (fromIndex: number, toIndex: number) => void;
}

interface UseDragAndDropPayload {
    payload: Payload;
    onDrop: (event: DragEvent) => void;
    onDragStart: (event: DragEvent) => void;
    onDragLeave: (event: DragEvent) => void;
    onDragOver: (event: DragEvent) => void;
}

export const useDragAndDrop = (props: UseDragAndDropProps): UseDragAndDropPayload => {
    const [payload, setPayload] = useSetState<Payload>(INITIAL_PAYLOAD);

    const onDragStart = useCallback(
        (event: DragEvent) => {
            // @ts-ignore
            const initialPosition = Number(event.currentTarget.dataset.position);

            setPayload({
                isDragging: true,
                draggedFrom: initialPosition,
            });

            // Без этого DnD не будет работать в Firefox.
            event.dataTransfer.setData('text/html', '');
        },
        [setPayload],
    );

    const onDragOver = useCallback(
        (event: DragEvent) => {
            event.preventDefault();

            // @ts-ignore
            const draggedTo = Number(event.currentTarget.dataset.position);

            if (draggedTo !== payload.draggedTo) {
                setPayload({ draggedTo });
            }
        },
        [payload, setPayload],
    );

    const onDrop = useCallback(() => {
        setPayload(INITIAL_PAYLOAD);

        if (payload.draggedFrom !== null && payload.draggedTo !== null) {
            props.onDrop(payload.draggedFrom, payload.draggedTo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload, setPayload, props.onDrop]);

    const onDragLeave = useCallback(() => {
        setPayload({ draggedTo: null });
    }, [setPayload]);

    return {
        payload,
        onDrop,
        onDragStart,
        onDragLeave,
        onDragOver,
    };
};
